import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { buildClosedGmMatchState, endOfDay, isGmMatchExpired, startOfDay } from './gmMatchClosing';
import { calculateGmMatchWeeklyScores } from './gmMatchScoring';

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const resolveFallbackRoster = async ({ db, rosterId, roster = null }) => {
  if (roster?.id) return roster;
  if (!rosterId) return null;

  const rosterSnap = await getDoc(doc(db, 'rosters', rosterId));
  return rosterSnap.exists() ? { id: rosterSnap.id, ...rosterSnap.data() } : null;
};

const loadMatchOwners = async ({ db, match }) => {
  const ownerUids = [...new Set(
    (match?.participants || [])
      .map((participant) => participant.ownerUid)
      .filter(Boolean)
  )];

  if (ownerUids.length === 0) return {};

  const ownerDocs = await Promise.all(
    ownerUids.map((ownerUid) => getDoc(doc(db, 'users', ownerUid)))
  );

  return ownerDocs.reduce((acc, ownerDoc) => {
    if (ownerDoc.exists()) {
      acc[ownerDoc.id] = ownerDoc.data();
    }
    return acc;
  }, {});
};

const loadMatchFactions = async ({ db, match }) => {
  const factionIds = [...new Set(
    (match?.participants || [])
      .map((participant) => participant.factionId)
      .filter(Boolean)
  )];

  if (factionIds.length === 0) return {};

  const factionDocs = await Promise.all(
    factionIds.map((factionId) => getDoc(doc(db, 'factions', factionId)))
  );

  return factionDocs.reduce((acc, factionDoc) => {
    if (factionDoc.exists()) {
      acc[factionDoc.id] = { id: factionDoc.id, ...factionDoc.data() };
    }
    return acc;
  }, {});
};

const loadRostersByFaction = async ({
  db,
  match,
  factionsById,
  fallbackRoster = null,
}) => {
  const rosterIds = [...new Set(
    (match?.participants || [])
      .map((participant) => factionsById[participant.factionId]?.rosterId)
      .filter(Boolean)
      .filter((rosterId) => rosterId !== fallbackRoster?.id)
  )];

  const rosterDocs = await Promise.all(
    rosterIds.map((rosterId) => getDoc(doc(db, 'rosters', rosterId)))
  );

  const rosterMap = rosterDocs.reduce((acc, rosterDoc) => {
    if (rosterDoc.exists()) {
      acc[rosterDoc.id] = { id: rosterDoc.id, ...rosterDoc.data() };
    }
    return acc;
  }, {});

  return (match?.participants || []).reduce((acc, participant) => {
    const faction = factionsById[participant.factionId];
    const factionRosterId = faction?.rosterId;

    if (factionRosterId && rosterMap[factionRosterId]) {
      acc[participant.factionId] = rosterMap[factionRosterId];
      return acc;
    }

    if (fallbackRoster) {
      acc[participant.factionId] = fallbackRoster;
    }

    return acc;
  }, {});
};

const loadMatchShowEntries = async ({ db, match }) => {
  const weekStart = match?.weekStart ? startOfDay(toDate(match.weekStart)) : null;
  const weekEnd = match?.weekEnd ? endOfDay(toDate(match.weekEnd)) : null;

  if (!weekStart || !weekEnd) {
    throw new Error('Periodo match non valido');
  }

  const showsQuery = query(
    collection(db, 'shows'),
    where('data', '>=', Timestamp.fromDate(weekStart)),
    where('data', '<=', Timestamp.fromDate(weekEnd))
  );
  const showsSnap = await getDocs(showsQuery);

  return Promise.all(
    showsSnap.docs.map(async (showDoc) => {
      const participationsSnap = await getDocs(collection(db, 'shows', showDoc.id, 'participations'));

      return {
        show: { id: showDoc.id, ...showDoc.data() },
        participations: participationsSnap.docs.map((participationDoc) => ({
          id: participationDoc.id,
          ...participationDoc.data(),
        })),
      };
    })
  );
};

const buildParticipantDisplayName = (participant, ownersById = {}) => {
  const owner = participant?.ownerUid ? ownersById[participant.ownerUid] : null;
  return owner?.username || owner?.displayName || owner?.email || participant?.factionName || 'Fazione';
};

export const calculateClosedStateForGmMatch = async ({
  db,
  match,
  rosterId = null,
  roster = null,
  closedAt = new Date(),
}) => {
  const [fallbackRoster, ownersById, factionsById] = await Promise.all([
    resolveFallbackRoster({ db, rosterId, roster }),
    loadMatchOwners({ db, match }),
    loadMatchFactions({ db, match }),
  ]);

  const [rostersByFactionId, showEntries] = await Promise.all([
    loadRostersByFaction({
      db,
      match,
      factionsById,
      fallbackRoster,
    }),
    loadMatchShowEntries({ db, match }),
  ]);

  const weeklyScores = calculateGmMatchWeeklyScores({
    match,
    factionsById,
    rostersByFactionId,
    showEntries,
  });

  return buildClosedGmMatchState({
    match,
    weeklyScoresByFaction: weeklyScores,
    getParticipantDisplayName: (participant) => buildParticipantDisplayName(participant, ownersById),
    closedAt,
  });
};

export const autoCloseExpiredGmMatches = async ({
  db,
  rosterId = null,
  matches = [],
  roster = null,
  persist = false,
  closedAt = new Date(),
}) => {
  const expiredMatches = (matches || []).filter((match) => isGmMatchExpired(match, closedAt));

  if (expiredMatches.length === 0) {
    return {
      matches,
      changed: false,
    };
  }

  const fallbackRoster = await resolveFallbackRoster({ db, rosterId, roster });
  const replacements = {};

  for (const match of expiredMatches) {
    const closedState = await calculateClosedStateForGmMatch({
      db,
      match,
      rosterId,
      roster: fallbackRoster,
      closedAt,
    });

    if (persist && rosterId && match?.id) {
      await updateDoc(doc(db, 'rosters', rosterId, 'matches', match.id), {
        ...closedState,
        closedAt: Timestamp.fromDate(closedState.closedAt),
        updatedAt: Timestamp.now(),
      });
    }

    replacements[match.id] = {
      ...match,
      ...closedState,
      updatedAt: closedAt,
    };
  }

  return {
    matches: matches.map((match) => replacements[match.id] || match),
    changed: true,
  };
};
